import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    SettingOption,
    AppBlockPort,
    AppBlockLinkParameter,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';

export default class MathNode extends BaseNode {
    constructor() {
        super();
        this.name = 'Math';
        this.id = 'node_math';
        this.linkedFeature = 'general';

        this.inputs = [
            {
                name: 'Set',
                portId: 'set_value',
                params: [
                    {
                        name: 'value',
                        description: 'Value',
                        dataType: TibboDataType.CUSTOM,
                        default: '',
                    },
                ],
            },
        ];

        this.outputs = [
            {
                name: 'Set Complete',
                portId: 'math_done',
                params: [] as AppBlockLinkParameter[],
            },
        ];

        const settings: Setting[] = [
            {
                id: 'VARIABLE',
                name: 'Variable',
                settingType: SettingType.OPTION,
                required: true,
                refId: 'variable',
                default: '',
                options: [],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getInputs(): AppBlockPort[] {
        const project: ProjectConfiguration = this.project;
        let DATATYPE = TibboDataType.CUSTOM;
        const VARIABLE = project.getFeature(this.getSettingValue('VARIABLE'));
        if (VARIABLE !== undefined) {
            DATATYPE = VARIABLE.getSettingValue('DATATYPE');
        }
        return [{
            name: 'Set',
            portId: 'set_value',
            params: [
                {
                    name: 'value',
                    description: 'Value',
                    dataType: DATATYPE,
                    default: '',
                },
            ],
        }];
    }

    validate(): Array<ValidationMessage> {
        const errors: ValidationMessage[] = [];
        const INPUTS = this.getInputs();
        const project: ProjectConfiguration = this.project;
        const FEATURE = project.getFeature(this.getSettingValue('FEATURE'));
        if (FEATURE === undefined) {
            return errors;
        }
        const DATATYPE = FEATURE.getSettingValue('DATATYPE');
        const globals = project.getFeature('global_vars');

        if (globals) {
            for (let k = 0; k < INPUTS.length; k++) {
                const params = INPUTS[k].params;
                for (let l = 0; l < params.length; l++) {
                    switch (params[l].name) {
                        case 'value':
                            params[l].dataType = DATATYPE;
                            break;
                        default:

                            break;
                    }
                }
            }
        }

        return errors;
    }

    getSettingValue(settingId: string): string {
        if (settingId === 'DISPLAY_NAME') {
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            const project: ProjectConfiguration = this.project;
            const VARIABLE = project.getFeature(this.getSettingValue('VARIABLE'));
            if (VARIABLE) {
                let variableValue = '';
                const links = project.getNodeLinks(this, 'set_value');
                if (links.length > 0) {
                    const link = links[0];
                    variableValue = project.getNodeParameter(link, 'value');
                }
                return `Set ${VARIABLE.getSettingValue('VARNAME')} to ${variableValue}`;
            }
        }
        return super.getSettingValue(settingId);
    }

    getName(): string {
        const project: ProjectConfiguration = this.project;
        const VARIABLE = project.getFeature(this.getSettingValue('VARIABLE'));
        if (VARIABLE) {
            let variableValue = '';
            const links = project.getNodeLinks(this, 'set_value');
            if (links.length > 0) {
                const link = links[0];
                variableValue = project.getNodeParameter(link, 'value');
            }
            return `Set ${VARIABLE.getSettingValue('VARNAME')} to ${variableValue}`;
        }
        return super.getName();
    }

    getCodeBlocks(): CodeBlock[] {
        const blocks: CodeBlock[] = [];
        const project: ProjectConfiguration = this.project;
        const variable = project.getFeature(this.getSettingValue('VARIABLE'));
        if (!variable) {
            return blocks;
        }
        const VARNAME = variable.getSettingValue('VARNAME');

        const links = project.getNodeLinks(this, 'set_value');
        for (let i = 0; i < links.length; i += 1) {
            const link = links[i];
            let doneTemplate = '';
            const tmpLinks = project.getNodeLinks(this, 'math_done');
            if (tmpLinks.length > 0) {
                doneTemplate += `'****${tmpLinks[0].getSourceId()}****'`;
            }
            blocks.push({
                location: link.getSourceId(),
                blockType: BlockType.CODE,
                id: link.getID(),
                template: `var_${VARNAME}_set((${project.getNodeParameter(link, 'value')}))
                ${doneTemplate}`,
            });
        }

        return blocks;
    }

    getDetails(): string {
        return `#### Math
Performs a calculation and assigns the result to a **Variable**
`;
    }
}
